import { useState, useEffect, useRef } from 'react';
import PixelAgent from './PixelAgent';

const SCALE = 2;
const px = (n) => n * SCALE;

const PHASE_TO_CHAR = {
  ideation: 1, research: 2, prd: 3, design: 4, code: 5,
};

const PHASE_LABELS = {
  ideation: 'Ideating', research: 'Researching', prd: 'Writing PRD',
  design: 'Designing', code: 'Coding', auto: 'Orchestrating',
};

const AGENT_NAMES = ['Super Agent', 'Ideation Agent', 'Research Agent', 'PRD Agent', 'Design Agent', 'Code Agent'];

// Wandering bounds (left, bottom in px within the scene)
const WANDER_BOUNDS = { minX: 20, maxX: 380, minBottom: 55, maxBottom: 180 };

function randomWaypoint(currentX, currentBottom) {
  const x = Math.floor(Math.random() * (WANDER_BOUNDS.maxX - WANDER_BOUNDS.minX)) + WANDER_BOUNDS.minX;
  const bottom = Math.floor(Math.random() * (WANDER_BOUNDS.maxBottom - WANDER_BOUNDS.minBottom)) + WANDER_BOUNDS.minBottom;
  return { x, bottom };
}

function getDirection(from, to) {
  const dx = to.x - from.x;
  const dy = to.bottom - from.bottom;
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'up' : 'down';
}

function useMover(initialX, initialBottom, enabled) {
  const [pos, setPos] = useState({ x: initialX, bottom: initialBottom });
  const [dir, setDir] = useState('down');
  const posRef = useRef({ x: initialX, bottom: initialBottom });

  useEffect(() => {
    if (!enabled) return;
    const move = () => {
      const next = randomWaypoint(posRef.current.x, posRef.current.bottom);
      setDir(getDirection(posRef.current, next));
      posRef.current = next;
      setPos(next);
    };
    move();
    const interval = setInterval(move, 2000 + Math.random() * 1500);
    return () => clearInterval(interval);
  }, [enabled]);

  return { pos, dir };
}

const Furniture = ({ src, w, h, style = {} }) => (
  <img src={src} alt="" draggable={false}
    style={{ width: px(w), height: px(h), imageRendering: 'pixelated', display: 'block', ...style }} />
);

const ActiveBadge = ({ label }) => (
  <div style={{
    position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)',
    whiteSpace: 'nowrap', background: '#2563EB', borderRadius: 8,
    padding: '2px 7px', fontSize: 9, fontWeight: 800, color: 'white',
    boxShadow: '0 2px 8px rgba(37,99,235,0.4)', pointerEvents: 'none',
  }}>⚡ {label}</div>
);

const ActivityBubble = ({ text }) => (
  <div style={{
    position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.8)',
    borderRadius: 8, padding: '2px 6px', fontSize: 9, fontWeight: 700,
    color: '#475569', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', pointerEvents: 'none',
  }}>{text}</div>
);

const IDLE_ACTIVITIES = [
  '☕ Coffee break', '📖 Reading docs', '💬 Slack-ing',
  '🎧 In the zone', '📝 Taking notes', '🤔 Overthinking',
];

const DeskStation = ({ charIndex, isActive, phaseName, actSeed, left, top }) => (
  <div style={{ position: 'absolute', left, top }}>
    {isActive && (
      <div style={{ position: 'absolute', inset: -6, borderRadius: 12,
        background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', zIndex: 0 }} />
    )}
    <div style={{ position: 'relative', zIndex: 1 }}>
      <Furniture src="/assets/furniture/DESK/DESK_FRONT.png" w={48} h={32} />
      <div style={{ position: 'absolute', left: px(12), top: -px(14) }}>
        <img src={`/assets/furniture/PC/PC_FRONT_O${isActive ? 'N_1' : 'FF'}.png`} alt=""
          draggable={false} style={{ width: px(16), height: px(32), imageRendering: 'pixelated' }} />
      </div>
      <div style={{ position: 'absolute', left: px(14), top: px(4), zIndex: 2 }}>
        <div style={{ position: 'relative' }}>
          {isActive ? <ActiveBadge label={phaseName} /> : <ActivityBubble text={IDLE_ACTIVITIES[actSeed % IDLE_ACTIVITIES.length]} />}
          <PixelAgent charIndex={charIndex} mode={isActive ? 'type' : 'walk'} scale={SCALE} direction="down" />
        </div>
      </div>
    </div>
  </div>
);

// Wandering agent with autonomous movement
const WanderingAgent = ({ charIndex, isActive, phaseName, actSeed, initialX, initialBottom }) => {
  const { pos, dir } = useMover(initialX, initialBottom, !isActive);

  return (
    <div style={{
      position: 'absolute',
      left: isActive ? initialX : pos.x,
      bottom: isActive ? initialBottom : pos.bottom,
      zIndex: 3,
      transition: isActive ? 'none' : 'left 1.8s linear, bottom 1.8s linear',
    }}>
      <div style={{ position: 'relative' }}>
        {isActive ? <ActiveBadge label={phaseName} /> : <ActivityBubble text={IDLE_ACTIVITIES[actSeed % IDLE_ACTIVITIES.length]} />}
        <PixelAgent
          charIndex={charIndex}
          mode={isActive ? 'type' : 'walk'}
          scale={SCALE}
          direction={isActive ? 'down' : dir}
        />
      </div>
    </div>
  );
};

const OfficeScene = ({ currentPhase, selectedAgent }) => {
  const [actSeeds, setActSeeds] = useState([0, 1, 2, 3, 4, 5]);

  useEffect(() => {
    const t = setInterval(() => {
      setActSeeds(prev => prev.map((s, i) => (s + i + 2) % IDLE_ACTIVITIES.length));
    }, 4000);
    return () => clearInterval(t);
  }, []);

  let activeChar = PHASE_TO_CHAR[currentPhase] ?? 0;
  if (!currentPhase) activeChar = 0;
  const phaseName = PHASE_LABELS[currentPhase] || AGENT_NAMES[activeChar];
  const isActive = (i) => i === activeChar;

  return (
    <div style={{
      position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
      backgroundColor: '#c8b89a',
      backgroundImage: 'url(/assets/floors/floor_1.png)',
      backgroundRepeat: 'repeat',
      backgroundSize: `${px(16)}px ${px(16)}px`,
      imageRendering: 'pixelated',
    }}>
      {/* Wall strip */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: px(16),
        backgroundImage: 'url(/assets/walls/wall_0.png)',
        backgroundRepeat: 'repeat-x', backgroundSize: `${px(16)}px ${px(16)}px`,
        imageRendering: 'pixelated', zIndex: 1,
      }} />

      {/* Wall decor */}
      <div style={{ position: 'absolute', top: -px(4), left: 32, zIndex: 2 }}>
        <Furniture src="/assets/furniture/CLOCK/CLOCK.png" w={16} h={32} />
      </div>
      <div style={{ position: 'absolute', top: -px(4), left: 140, zIndex: 2 }}>
        <Furniture src="/assets/furniture/WHITEBOARD/WHITEBOARD.png" w={32} h={32} />
      </div>
      <div style={{ position: 'absolute', top: -px(2), left: 320, zIndex: 2 }}>
        <Furniture src="/assets/furniture/BOOKSHELF/BOOKSHELF.png" w={32} h={16} />
      </div>

      {/* Desk row — chars 0, 1, 2 */}
      <DeskStation charIndex={0} isActive={isActive(0)} phaseName={phaseName} actSeed={actSeeds[0]} left={16}  top={px(14)} />
      <DeskStation charIndex={1} isActive={isActive(1)} phaseName={phaseName} actSeed={actSeeds[1]} left={140} top={px(14)} />
      <DeskStation charIndex={2} isActive={isActive(2)} phaseName={phaseName} actSeed={actSeeds[2]} left={264} top={px(14)} />

      {/* Coffee table */}
      <div style={{ position: 'absolute', left: 80, bottom: 36, zIndex: 1 }}>
        <Furniture src="/assets/furniture/COFFEE_TABLE/COFFEE_TABLE.png" w={32} h={32} />
        <div style={{ position: 'absolute', top: px(4), left: px(8) }}>
          <Furniture src="/assets/furniture/COFFEE/COFFEE.png" w={16} h={16} />
        </div>
      </div>

      {/* Wandering agents — chars 3, 4, 5 */}
      <WanderingAgent charIndex={3} isActive={isActive(3)} phaseName={phaseName} actSeed={actSeeds[3]} initialX={60}  initialBottom={120} />
      <WanderingAgent charIndex={4} isActive={isActive(4)} phaseName={phaseName} actSeed={actSeeds[4]} initialX={200} initialBottom={110} />
      <WanderingAgent charIndex={5} isActive={isActive(5)} phaseName={phaseName} actSeed={actSeeds[5]} initialX={340} initialBottom={100} />

      {/* Corner plants */}
      <div style={{ position: 'absolute', right: 8, top: px(14), zIndex: 1 }}>
        <Furniture src="/assets/furniture/PLANT/PLANT.png" w={16} h={32} />
      </div>
      <div style={{ position: 'absolute', left: 8, bottom: 16, zIndex: 1 }}>
        <Furniture src="/assets/furniture/LARGE_PLANT/LARGE_PLANT.png" w={32} h={48} />
      </div>
      <div style={{ position: 'absolute', right: 8, bottom: 16, zIndex: 1 }}>
        <Furniture src="/assets/furniture/PLANT/PLANT.png" w={16} h={32} />
      </div>

      {/* Status bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(4px)',
        borderTop: '1px solid #E2E8F0', padding: '5px 12px',
        display: 'flex', alignItems: 'center', gap: 8, zIndex: 10,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2563EB',
          animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
          {AGENT_NAMES[activeChar]} is {phaseName.toLowerCase()}
          <span style={{ color: '#94A3B8', fontWeight: 400 }}> · Preview will appear when ready</span>
        </span>
      </div>
    </div>
  );
};

export default OfficeScene;
